import { useEffect, useState, useRef } from 'react';
import { motion, useSpring, useTransform, useInView } from 'framer-motion';

interface CountUpProps {
  end: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}

export function CountUp({
  end,
  duration = 2,
  suffix = '',
  prefix = '',
  decimals = 0,
}: CountUpProps) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.1,
  });

  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    if (inView && !hasStarted) {
      setHasStarted(true);
    }
  }, [inView, hasStarted]);

  // We use useSpring to animate the number from 0 to 'end'
  const springValue = useSpring(0, {
    bounce: 0,
    duration: duration * 1000,
  });

  // When 'hasStarted' becomes true, we set the spring target to 'end'
  useEffect(() => {
    if (hasStarted) {
      springValue.set(end);
    }
  }, [hasStarted, end, springValue]);

  // Format the raw number from the spring
  const roundedValue = useTransform(springValue, (latest) => {
    return prefix + latest.toFixed(decimals) + suffix;
  });

  return (
    <motion.span ref={ref}>
      {roundedValue}
    </motion.span>
  );
}
